import { useEffect, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";
import { Button } from "@accredo/fe-shared/theme/components";
import {
  readRecentCustomers,
  rememberRecentCustomer,
} from "../customerScope.js";

const memberLabel = (count) => (count === 1 ? "1 Kunde" : `${count} Kunden`);

const toSelection = ({ id, name, address, member_count }) => ({
  id,
  name,
  address,
  member_count,
});

/**
 * The customer picker of internal users: a header button naming the current
 * Kundenansicht, opening a dialog that searches all Kunden, shown as groups
 * (the root as a selectable row, its members indented below it). Before
 * anything is typed it offers the customers selected most recently.
 *
 * @param {{ id: number, name: string, member_count: number } | null} props.selection
 * @param {(selection: object | null) => void} props.onChange
 */
export default function CustomerPicker({ selection, onChange }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const debounced_input = useDebounce(input, 300);
  const [groups, setGroups] = useState([]);
  const [is_loading, setIsLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const is_searching = input.trim() !== "";
  // The debounced value lags behind a cleared field; an empty field never searches.
  const query = is_searching ? debounced_input.trim() : "";

  useEffect(() => {
    if (!open || !query) {
      setGroups([]);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    backendGet("/portal/customers", {
      label: "Kunden",
      options: { params: { q: query } },
    }).then((response) => {
      if (!cancelled) {
        setGroups(response?.data ?? []);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, query]);

  const openDialog = () => {
    setRecent(readRecentCustomers());
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setInput("");
  };

  const choose = (selection) => {
    if (selection) {
      rememberRecentCustomer(selection);
    }
    close();
    onChange(selection);
  };

  const renderEntry = (entry, level) => (
    <ListItemButton
      key={entry.id}
      selected={selection?.id === entry.id}
      onClick={() => choose(toSelection(entry))}
      sx={{ pl: level ? 5 : 2 }}
    >
      <ListItemText
        primary={entry.name}
        secondary={
          [
            entry.address,
            level === 0 && entry.member_count > 0
              ? `inkl. ${memberLabel(entry.member_count)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || undefined
        }
        primaryTypographyProps={{ fontWeight: level ? 400 : 600 }}
        secondaryTypographyProps={{ variant: "caption" }}
      />
    </ListItemButton>
  );

  return (
    <>
      <Button
        variant="neutral"
        endIcon={<ExpandMoreIcon />}
        onClick={openDialog}
        sx={{ minWidth: 0, maxWidth: "100%", textTransform: "none", py: 0.5 }}
      >
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 0,
            lineHeight: 1.2,
          }}
        >
          <span style={{ fontSize: "0.625rem" }}>Kundenansicht</span>
          <span
            style={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selection ? selection.name : "Kunde auswählen"}
          </span>
        </span>
      </Button>

      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>
          Kundenansicht
          <IconButton
            aria-label="Schließen"
            onClick={close}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Kunde suchen …"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            inputProps={{ "aria-label": "Kunde suchen" }}
            sx={{ mt: 1 }}
          />
          {!is_searching ? (
            recent.length ? (
              <List
                aria-label="Zuletzt gewählt"
                subheader={
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", px: 2, pt: 1 }}
                  >
                    Zuletzt gewählt
                  </Typography>
                }
                sx={{ mt: 1 }}
              >
                {recent.map((entry) => renderEntry(entry, 0))}
              </List>
            ) : (
              <Typography
                color="text.secondary"
                sx={{ mt: 3, textAlign: "center" }}
              >
                Namen eingeben, um Kunden zu suchen
              </Typography>
            )
          ) : is_loading || query !== input.trim() ? (
            <CircularProgress
              size={24}
              sx={{ display: "block", mx: "auto", my: 3 }}
            />
          ) : groups.length ? (
            <List
              aria-label="Kunden"
              sx={{ mt: 1, maxHeight: 400, overflowY: "auto" }}
            >
              {groups.flatMap(({ root, members }) => [
                renderEntry(root, 0),
                ...members.map((member) =>
                  renderEntry({ ...member, member_count: 0 }, 1)
                ),
              ])}
            </List>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ mt: 3, textAlign: "center" }}
            >
              Keine Kunden gefunden
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="tertiary"
            disabled={!selection}
            onClick={() => choose(null)}
          >
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
